import { useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { CORE_FORM_FIELDS } from '../SourceCreate.constants';
import { useSourceCreateState, useSourceDialog } from '../SourceCreate.hooks';
import { useAddThumbprintId } from './useCloudSourceEditDetails.hooks';
import { useSourceDefaults } from './useSourceDefaults.hooks';
import { useSourceFormHandlers } from './useSourceFormHandlers.hooks';
import { useSourceFormQueries } from './useSourceFormQueries.hooks';

/**
 * Main hook for managing cloud source form orchestration.
 *
 * Responsibilities:
 * - Orchestrates data fetching, state management, and event handling
 * - Manages source type transitions and schema resets
 * - Provides unified API for form components
 *
 * Delegates to specialized hooks:
 * - useSourceFormQueries: Data fetching
 * - useSourceFormHandlers: Event handling
 * - useSourceDefaults: Default value initialization
 */
const useCloudSourceForm = (isCustomerCloudSourcePage?: boolean) => {
  const { setValue, unregister, getValues } = useFormContext();
  const [isResettingSourceType, setIsResettingSourceType] = useState(false);

  const {
    connectionInputSchema,
    setConnectionInputSchema,
    selectedOrgData,
    selectedSourceTypeId,
    setSelectedSourceTypeId,
    sourceTypeBasicData,
    categoryMasterList,
    selectedSourceDetails,
    categories,
    setCategories,
    region: regions,
    setRegion: setRegions,
    selectedSubTimeInterval,
    setSelectedSubTimeInterval,
    selectedTimeInterval,
    setSelectedTimeInterval,
    subTimeIntervalOptions,
    setSubTimeIntervalOptions,
    cloudProvider: cloudProviders,
    setCloudProvider: setCloudProviders,
    deviceAlertFields,
    setDeviceAlertFields,
    sourceStatus,
    setSourceStatus,
    selectedSubSourceType,
    setSelectedSubSourceType,
    selectedIngestConfigTab,
    setSelectedIngestConfigTab,
    setVulnFields
  } = useSourceCreateState();

  const { mode, authenticationMode, newCertificate, regenerateCertificate } =
    useSourceDialog();

  // Delegate to specialized hooks
  const queries = useSourceFormQueries(mode, isCustomerCloudSourcePage);
  const handlers = useSourceFormHandlers();

  // Initialize defaults
  useSourceDefaults(connectionInputSchema);

  // Handle thumbprint ID addition
  useAddThumbprintId(
    mode,
    selectedSourceDetails,
    connectionInputSchema,
    setConnectionInputSchema
  );

  /**
   * Handles source type transitions with a 5-step process to ensure clean form state.
   *
   * This complex reset is necessary because:
   * 1. Different source types have completely different schemas and fields
   * 2. React Hook Form needs fields unregistered to prevent validation errors
   * 3. State atoms must be cleared to prevent stale data display
   *
   * @param srcTypeId - The new source type ID to transition to
   */
  const updateSourceTypeSchema = useCallback(
    (srcTypeId: number) => {
      const alreadySelected =
        selectedSourceTypeId === srcTypeId &&
        Object.keys(connectionInputSchema || {}).length > 0;

      if (alreadySelected) {
        return;
      }

      // STEP 1: Set resetting flag to prevent any renders during transition
      // This prevents flickering and partial rendering of old/new fields
      setIsResettingSourceType(true);

      // STEP 2: Clear the schema to prevent stale data from previous source type
      // Without this, old fields may briefly render with new schema
      setConnectionInputSchema({});

      // STEP 3: Unregister all dynamic fields while preserving core fields
      // Core fields (org, device, etc.) are consistent across all source types
      const allFields = Object.keys(getValues());
      const fieldsToUnregister = allFields.filter(
        (field) =>
          !CORE_FORM_FIELDS.includes(field as (typeof CORE_FORM_FIELDS)[number])
      );

      if (fieldsToUnregister.length > 0) {
        fieldsToUnregister.forEach((field) => {
          unregister(field, {
            keepValue: false,
            keepError: false,
            keepDirty: false,
            keepTouched: false,
            keepIsValid: false,
            keepDefaultValue: false
          });
        });
      }

      // STEP 4: Clear all source-specific state variables
      // These state atoms are source-type specific and must be reset
      setCategories([]);
      setRegions([]);
      setCloudProviders([]);
      setDeviceAlertFields([]);
      setVulnFields([]);

      // Keep React Hook Form as the source of truth for submission.
      // Relying on a hidden TextField value is brittle across RHF/MUI updates.
      setValue('source_type_id', srcTypeId, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true
      });

      const sourceTypeName = sourceTypeBasicData?.find(
        (sourceType) => sourceType.id === srcTypeId
      )?.name;

      setValue('source_type_name', sourceTypeName || '', {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: false
      });

      // STEP 5: Update source type ID - this triggers React key change and schema fetch
      setSelectedSourceTypeId(srcTypeId);
      setSelectedIngestConfigTab('ingest_method_config');

      // Clear setting flag to allow new fields to render
      setIsResettingSourceType(false);
    },
    [
      unregister,
      getValues,
      selectedSourceTypeId,
      connectionInputSchema,
      setCategories,
      setRegions,
      setCloudProviders,
      setDeviceAlertFields,
      setVulnFields,
      setConnectionInputSchema,
      setSelectedSourceTypeId,
      setSelectedIngestConfigTab,
      setValue,
      sourceTypeBasicData
    ]
  );

  const handleSourceTypeChange = useCallback(
    (sourceTypeId: number) => {
      updateSourceTypeSchema(sourceTypeId);
    },
    [updateSourceTypeSchema]
  );

  // Return unified API combining all responsibilities
  return {
    // Core state
    mode,
    connectionInputSchema,
    setConnectionInputSchema,
    selectedOrgData,
    selectedSourceTypeId,
    sourceTypeBasicData,
    categoryMasterList,
    selectedSourceDetails,
    isResettingSourceType,

    // Categories, regions, providers
    categories,
    setCategories,
    regions,
    cloudProviders,
    setCloudProviders,
    deviceAlertFields,

    // Time intervals
    selectedSubTimeInterval,
    setSelectedSubTimeInterval,
    selectedTimeInterval,
    setSelectedTimeInterval,
    subTimeIntervalOptions,
    setSubTimeIntervalOptions,

    // Source type and configuration
    selectedSubSourceType,
    setSelectedSubSourceType,
    selectedIngestConfigTab,
    setSelectedIngestConfigTab,
    sourceStatus,
    setSourceStatus,

    // Authentication
    authenticationMode,
    newCertificate,
    regenerateCertificate,

    // Event handlers (from useSourceFormHandlers)
    handleSourceTypeChange,
    handleCategoryChange: handlers.handleCategoryChange,
    handleRegionChange: handlers.handleRegionChange,
    handleCloudProviderChange: handlers.handleCloudProviderChange,
    handleDeviceAlertFieldsChange: handlers.handleDeviceAlertFieldsChange,
    handleAuthenticationModeChange: handlers.handleAuthenticationModeChange,
    handleCertificationModeChange: handlers.handleCertificationModeChange,
    downladCertificate: handlers.downladCertificate,
    regenerate: handlers.regenerate,

    // Query results (from useSourceFormQueries)
    sourceTypeBasicQuery: queries.sourceTypeBasicQuery,
    sourceTypeSchemaQuery: queries.sourceTypeSchemaQuery,
    sourceLogTypeQuery: queries.sourceLogTypeQuery,
    sourceDetailQuery: queries.sourceDetailQuery,
    limaDevicesQuery: queries.limaDevicesQuery
  };
};

export default useCloudSourceForm;
